import React, { FC, useEffect, useRef, useState } from 'react';

import { Pause, Play } from '@gravity-ui/icons';
import ChevronDown from '@gravity-ui/icons/ChevronDown';
import { Button, Icon, Select } from '@gravity-ui/uikit';
import { TtsVoice } from 'client/types';

import styles from './SelectAudio.module.scss';

interface SelectAudioProps {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    voices: TtsVoice[];
    className?: string;
    placeholder: string;
}

export const SelectAudio: FC<SelectAudioProps> = ({ value, onChange, voices, disabled, className, placeholder }) => {
    const voice = value && voices.find((x) => x.name === value);

    return (
        <Select
            className={className}
            name="voice"
            value={[value]}
            options={voices.map(({ name, title }) => ({ value: name, content: title }))}
            placeholder="Выбрать голос"
            disabled={disabled}
            size="xl"
            onUpdate={(selectedValues) => onChange(selectedValues[0])}
            renderControl={({ onClick, onKeyDown, ref }) => {
                return (
                    <Button
                        ref={ref}
                        className={styles.controlButton}
                        onClick={onClick}
                        extraProps={{ onKeyDown }}
                        view="outlined"
                        width="max"
                        size="xl"
                        disabled={disabled}
                    >
                        <div className={styles.content}>
                            {voice ? voice.title : <span className={styles.placeholder}>{placeholder}</span>}
                            <div className={styles.iconsBlock}>
                                {voice && voice.samples && voice.samples.length ? (
                                    <StaticAudio key={voice.name} sources={voice.samples} />
                                ) : null}
                                <Icon data={ChevronDown} />
                            </div>
                        </div>
                    </Button>
                );
            }}
        />
    );
};

interface StaticAudioProps {
    sources: Array<{ src: string; type: string }>;
}

const StaticAudio: FC<StaticAudioProps> = ({ sources }) => {
    const [paused, setPaused] = useState(true);
    const [playIndex, setPlayIndex] = useState(0);

    const audioRef = useRef<HTMLAudioElement>(null);

    useEffect(() => {
        const audioPlayer = audioRef.current;
        if (audioPlayer) {
            audioPlayer.addEventListener('play', onPlay);
            audioPlayer.addEventListener('pause', onPause);
            audioPlayer.addEventListener('ended', onEnded);
        }

        return () => {
            if (audioPlayer) {
                audioPlayer.removeEventListener('play', onPlay);
                audioPlayer.removeEventListener('pause', onPause);
                audioPlayer.removeEventListener('ended', onEnded);
            }
        };
    }, []);

    const onPause = () => setPaused(true);
    const onPlay = () => setPaused(false);
    const onEnded = () => {
        setPlayIndex((index) => ++index);
        if (audioRef.current) {
            audioRef.current.load();
        }
    };

    const handleToggle = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();
        if (audioRef.current && audioRef.current.paused) {
            audioRef.current.play();
        } else if (audioRef.current) {
            audioRef.current.pause();
        }
    };

    const source = sources.length ? sources[playIndex % sources.length] : null;
    if (!source) {
        return null;
    }

    return (
        <>
            <div className={styles.playControl} onClick={handleToggle} tabIndex={0}>
                {paused ? <Icon data={Play} /> : <Icon data={Pause} />}
            </div>
            <audio ref={audioRef}>
                <source src={source.src} type={source.type} />
            </audio>
        </>
    );
};
